import type { Exercise } from '../../types.ts';
export const batch3Reading: Exercise[] = [
  {
    id: 'reading-nyos',
    category: 'reading',
    title: 'Lake Nyos disaster',
    topic: 'Lake Nyos disaster',
    difficulty: 'Challenging',
    article: {
      title: 'Lake Nyos disaster',
      url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'A lake in Cameroon released a lethal cloud of gas in 1986. The assigned sections follow how gas accumulated, how the release spread, and how engineers designed a controlled escape route for it.',
      scope: {
        wordCount: 897,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster',
            wordCount: 128,
            anchors: [''],
          },
          {
            title: 'Eruption and gas release',
            url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Eruption_and_gas_release',
            wordCount: 482,
            anchors: ['Eruption_and_gas_release'],
          },
          {
            title: 'Mitigation efforts',
            url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Mitigation_efforts',
            wordCount: 157,
            anchors: ['Mitigation_efforts'],
          },
          {
            title: 'Similar danger suspected at Lake Kivu',
            url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Similar_danger_suspected_at_Lake_Kivu',
            wordCount: 130,
            anchors: ['Similar_danger_suspected_at_Lake_Kivu'],
          },
        ],
      },
    },
    reading: {
      title: 'Lake Nyos disaster — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'release',
          title: 'Pressure and a self-amplifying release',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Deep lake water can hold dissolved gas under pressure. In the account of the Nyos disaster, gas-rich water reached shallower levels where the pressure was lower, allowing carbon dioxide to leave solution as bubbles. Rising and expanding bubbles could bring more deep water upward, creating a feedback that accelerated the release. The stored gas and the process that released it are distinct parts of the explanation.',
            },
            {
              kind: 'paragraph',
              text: 'The article does not establish one certain initiating trigger. It discusses a landslide, other disturbances and the possibility of an internally developing instability. Explaining how a release can sustain itself does not by itself prove which event started it on that particular night. A careful causal account can be strong about the propagation mechanism while remaining uncertain about initiation.',
            },
          ],
        },
        {
          id: 'terrain',
          title: 'Why the terrain mattered',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The released carbon-dioxide-rich cloud was denser than the surrounding air. After rising initially, it descended and moved through low terrain and valleys, exposing nearby settlements. Density here means mass per volume. Under the conditions described, gravity and terrain helped determine where the dense cloud accumulated; it did not behave like a plume that simply rose away and dispersed above the villages.',
            },
            {
              kind: 'paragraph',
              text: 'This connects a material property to the geographic pattern of harm. It is not a universal rule that every gas called volcanic must follow valleys: composition, temperature and mixing affect buoyancy. Nor does the color of the lake explain the cloud’s path. The article attributes the lake’s red color to deep iron-rich water reaching the surface and oxidizing, a different consequence of the disturbance.',
            },
          ],
        },
        {
          id: 'pipes',
          title: 'Controlled removal uses the same gas-release physics',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The mitigation pipes bring gas-rich deep water upward in a confined path. An initial pump starts the flow. As pressure falls and bubbles form inside the pipe, the bubbly column continues to rise, sustaining the process without continuous pumping. The intended outcome is gradual removal of stored gas before a large uncontrolled release can develop.',
            },
            {
              kind: 'paragraph',
              text: 'The engineering response therefore manages the hazard’s reservoir rather than merely shielding villages from a future cloud. It does not seal all the carbon dioxide permanently underwater or chemically change it into another substance. Calling a system self-sustaining refers to how the flow is driven after initiation; it is not a general claim that infrastructure never needs inspection or that all future risk disappears.',
            },
          ],
        },
        {
          id: 'evidence',
          title: 'A revised risk estimate is not proof of zero risk',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The Lake Kivu section describes a later study that challenged aspects of an earlier assessment, including how measurements were converted into gas partial pressures and how sensors were calibrated under high pressure. After addressing these problems, the later study did not find the previously suggested increase in eruption risk over time.',
            },
            {
              kind: 'paragraph',
              text: 'That is a claim about a trend inferred from measurements, not a declaration that the lake contains no dissolved gas or can never erupt. Scientific revision can change the estimated trajectory of a hazard without denying that the physical hazard exists. The question asks for this limited conclusion because extending “not increasing in that analysis” to “impossible” would discard the source’s qualification.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Lake Nyos disaster · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b3-nyos-1',
        prompt:
          'What allowed gas-rich deep water to release large amounts of carbon dioxide as it rose?',
        options: [
          {
            id: '0',
            text: 'Increasing sunlight transformed the water into gas.',
          },
          {
            id: '1',
            text: 'Lower pressure allowed dissolved gas to come out of solution.',
          },
          {
            id: '2',
            text: 'The surface added carbon dioxide faster than the deep water could absorb it.',
          },
          {
            id: '3',
            text: 'Oxidation converted dissolved iron directly into carbon dioxide.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The assigned account identifies reduced pressure during upward mixing as allowing dissolved carbon dioxide to effervesce.',
        sectionIds: ['release'],
        evidence:
          'The assigned account identifies reduced pressure during upward mixing as allowing dissolved carbon dioxide to effervesce. Verified in assigned source section: Eruption_and_gas_release.',
        sourceSection: {
          title: 'Wikipedia: Eruption and gas release',
          url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Eruption_and_gas_release',
        },
      },
      {
        id: 'b3-nyos-2',
        prompt:
          'Which conclusion about the event’s initiating trigger is supported by the article?',
        options: [
          {
            id: '0',
            text: 'A landslide is established as the unique cause.',
          },
          {
            id: '1',
            text: 'The initial pumping system caused the eruption.',
          },
          {
            id: '2',
            text: 'The trigger remains uncertain, with several possibilities discussed.',
          },
          {
            id: '3',
            text: 'A measured earthquake conclusively identifies the trigger.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The article explicitly says the catastrophic outgassing trigger is not known and discusses competing possibilities.',
        sectionIds: ['release'],
        evidence:
          'The article explicitly says the catastrophic outgassing trigger is not known and discusses competing possibilities. Verified in assigned source section: Eruption_and_gas_release.',
        sourceSection: {
          title: 'Wikipedia: Eruption and gas release',
          url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Eruption_and_gas_release',
        },
      },
      {
        id: 'b3-nyos-3',
        prompt: 'Why did low-lying villages face particular danger from the gas cloud?',
        options: [
          {
            id: '0',
            text: 'The dense carbon-dioxide-rich cloud descended and followed valleys.',
          },
          {
            id: '1',
            text: 'The gas dissolved in river water and was carried downstream before release.',
          },
          {
            id: '2',
            text: 'Strong winds forced a buoyant cloud to remain below the ridges.',
          },
          {
            id: '3',
            text: 'Iron-rich groundwater in the villages generated most of the gas locally.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Carbon dioxide’s greater density than air helped the cloud hug the ground and travel down valleys.',
        sectionIds: ['terrain'],
        evidence:
          'Carbon dioxide’s greater density than air helped the cloud hug the ground and travel down valleys. Verified in assigned source section: Eruption_and_gas_release.',
        sourceSection: {
          title: 'Wikipedia: Eruption and gas release',
          url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Eruption_and_gas_release',
        },
      },
      {
        id: 'b3-nyos-4',
        prompt:
          'How could a degassing pipe continue to lift water after its initial pump was no longer needed?',
        options: [
          {
            id: '0',
            text: 'A continuously running pump was still required after the first bubbles formed.',
          },
          {
            id: '1',
            text: 'The pipe stored the gas at greater pressure deep in the lake.',
          },
          {
            id: '2',
            text: 'Water cooled inside the pipe, pulling the deeper water upward.',
          },
          {
            id: '3',
            text: 'Gas released inside the rising water sustained the upward flow.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The pipe becomes self-sustaining when release of gas lifts the effervescing water column.',
        sectionIds: ['pipes'],
        evidence:
          'The pipe becomes self-sustaining when release of gas lifts the effervescing water column. Verified in assigned source section: Mitigation_efforts.',
        sourceSection: {
          title: 'Wikipedia: Mitigation efforts',
          url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Mitigation_efforts',
        },
      },
      {
        id: 'b3-nyos-5',
        prompt:
          'What is the careful reading of the later Lake Kivu study described in the assignment?',
        options: [
          {
            id: '0',
            text: 'It proved gas eruptions cannot occur at any lake.',
          },
          {
            id: '1',
            text: 'After measurement corrections, it did not find that the risk was increasing over time.',
          },
          {
            id: '2',
            text: 'It established that the earlier measured concentration was exactly correct.',
          },
          {
            id: '3',
            text: 'It showed that carbon dioxide was absent from the lake.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The later assessment questioned measurement/conversion issues and found no apparent increase in risk, not zero possible risk.',
        sectionIds: ['evidence'],
        evidence:
          'The later assessment questioned measurement/conversion issues and found no apparent increase in risk, not zero possible risk. Verified in assigned source section: Similar_danger_suspected_at_Lake_Kivu.',
        sourceSection: {
          title: 'Wikipedia: Similar danger suspected at Lake Kivu',
          url: 'https://en.wikipedia.org/wiki/Lake_Nyos_disaster#Similar_danger_suspected_at_Lake_Kivu',
        },
      },
    ],
  },
  {
    id: 'reading-mincemeat',
    category: 'reading',
    title: 'Operation Mincemeat',
    topic: 'Operation Mincemeat',
    difficulty: 'Challenging',
    article: {
      title: 'Operation Mincemeat',
      url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'A wartime deception depended on much more than a false battle plan. Read how the planners built a believable identity, chose a route for information to reach the enemy, and made the planted documents fit that story.',
      scope: {
        wordCount: 2706,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat',
            wordCount: 345,
            anchors: [''],
          },
          {
            title: 'Background',
            url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat#Background',
            wordCount: 1091,
            anchors: [
              'Background',
              'Inspiration_for_Mincemeat',
              'British_Intelligence_and_the_inspiration_for_the_plan',
              'Military_situation',
            ],
          },
          {
            title: "Developing the plan; the corpse's new identity",
            url: "https://en.wikipedia.org/wiki/Operation_Mincemeat#Developing_the_plan;_the_corpse's_new_identity",
            wordCount: 674,
            anchors: ["Developing_the_plan;_the_corpse's_new_identity"],
          },
          {
            title: 'Deception documents',
            url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat#Deception_documents',
            wordCount: 596,
            anchors: ['Deception_documents'],
          },
        ],
      },
    },
    reading: {
      title: 'Operation Mincemeat — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'strategy',
          title: 'A deception needs a believable alternative',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The real Allied target was Sicily. The deception presented other Mediterranean targets as the serious invasion objectives and described Sicily as a cover target. This was designed to influence the deployment of German forces, rather than merely to conceal that an invasion would happen somewhere. The article places Mincemeat inside the broader Operation Barclay deception.',
            },
            {
              kind: 'paragraph',
              text: 'An alternative story was especially useful because Sicily was already an obvious strategic choice. A simple denial would have competed with that inference. The false plans instead supplied a reason for apparent preparations concerning Sicily while directing attention toward threats that German leaders already considered plausible. This explains why a cover story must account for evidence the target can already see.',
            },
          ],
        },
        {
          id: 'identity',
          title: 'Make the carrier fit the message',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The fictional William Martin had to look like someone who plausibly carried sensitive correspondence. An acting major was senior enough to be entrusted with documents but not so prominent that many people would expect to recognize him. His Royal Marine role also let inquiries flow through an authority the planners could manage.',
            },
            {
              kind: 'paragraph',
              text: 'Personal letters, receipts and other ordinary objects supplied corroborating detail around that identity. Their purpose was not to state the invasion plan repeatedly. They made an apparent life story that could survive casual examination and support the credibility of the carrier. An impressive rank alone would not do the same job; it could invite recognition or inconsistencies that the invented identity could not withstand.',
            },
          ],
        },
        {
          id: 'delivery',
          title: 'Explain how the documents would be discovered',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The planners wanted the papers to pass through Spanish hands to German intelligence. Spain’s formal neutrality did not imply that recovered information would remain inaccessible to the Germans. The assigned account describes earlier evidence of such a channel and the planned placement of the body off the Spanish coast.',
            },
            {
              kind: 'paragraph',
              text: 'The physical packaging also had to make discovery plausible. A briefcase could be noticed even if officials avoided searching the corpse’s pockets, but it needed a believable reason to travel with its carrier and a way to remain attached. The article describes both supporting documents and a secured chain. Information content, its carrier and the route of discovery were interdependent parts of the operation.',
            },
          ],
        },
        {
          id: 'documents',
          title: 'Natural correspondence and limited causal conclusions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The planted invasion information appeared in personal correspondence between senior officers. The planners wanted the target to be identified clearly but casually in a document that could plausibly travel by this route. They did not want an unnaturally convenient formal master plan whose presence would itself raise suspicion. The article describes revisions intended to make the correspondence sound natural.',
            },
            {
              kind: 'paragraph',
              text: 'The introduction reports evidence that the papers were read and the deception influenced German expectations. It also says the full effect of the operation is not known. Evidence of a successful information operation does not isolate its exact contribution to an entire campaign, which depended on many other decisions and conditions. That distinction prevents a strong historical story from becoming a claim of sole causation.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Operation Mincemeat · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b3-mincemeat-1',
        prompt: 'How did the planted plans portray Sicily?',
        options: [
          {
            id: '0',
            text: 'As the genuine objective, with Greece as a diversion',
          },
          {
            id: '1',
            text: 'As a neutral resupply location outside the invasion plans',
          },
          {
            id: '2',
            text: 'As a cover target, diverting attention from supposed attacks elsewhere',
          },
          {
            id: '3',
            text: 'As territory already captured before the operation began',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Sicily was the real target, but the planted correspondence represented it as cover for attacks elsewhere.',
        sectionIds: ['strategy'],
        evidence:
          'Sicily was the real target, but the planted correspondence represented it as cover for attacks elsewhere. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat',
        },
      },
      {
        id: 'b3-mincemeat-2',
        prompt: 'Why was an acting-major identity useful to the planners?',
        options: [
          {
            id: '0',
            text: 'It made the carrier the most publicly recognizable officer available.',
          },
          {
            id: '1',
            text: 'It fit sensitive-document duties without making him so prominent that recognition was expected.',
          },
          {
            id: '2',
            text: 'It prevented Spanish authorities from handling his possessions.',
          },
          {
            id: '3',
            text: 'It removed any need for supporting identity documents.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The rank balanced plausible access to sensitive material against the risk that a prominent person would be recognized.',
        sectionIds: ['identity'],
        evidence:
          "The rank balanced plausible access to sensitive material against the risk that a prominent person would be recognized. Verified in assigned source section: Developing_the_plan;_the_corpse's_new_identity.",
        sourceSection: {
          title: "Wikipedia: Developing the plan; the corpse's new identity",
          url: "https://en.wikipedia.org/wiki/Operation_Mincemeat#Developing_the_plan;_the_corpse's_new_identity",
        },
      },
      {
        id: 'b3-mincemeat-3',
        prompt:
          'What was the principal role of the personal letters and receipts carried with the body?',
        options: [
          {
            id: '0',
            text: 'To authenticate the fictional carrier through ordinary details',
          },
          {
            id: '1',
            text: 'To provide the actual Allied invasion timetable',
          },
          {
            id: '2',
            text: 'To instruct German intelligence explicitly to change its plans',
          },
          {
            id: '3',
            text: 'To replace the need for a military identity altogether',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Pocket litter supplied corroborating details of an invented life around the carrier of the planted correspondence.',
        sectionIds: ['identity'],
        evidence:
          "Pocket litter supplied corroborating details of an invented life around the carrier of the planted correspondence. Verified in assigned source section: Developing_the_plan;_the_corpse's_new_identity.",
        sourceSection: {
          title: "Wikipedia: Developing the plan; the corpse's new identity",
          url: "https://en.wikipedia.org/wiki/Operation_Mincemeat#Developing_the_plan;_the_corpse's_new_identity",
        },
      },
      {
        id: 'b3-mincemeat-4',
        prompt: 'Why was Spain a plausible route for the documents to reach German intelligence?',
        options: [
          {
            id: '0',
            text: 'Spain had already agreed publicly to deliver this particular body.',
          },
          {
            id: '1',
            text: 'Every neutral state was required to share recovered military papers.',
          },
          {
            id: '2',
            text: 'The operation relied on a direct British courier to Berlin.',
          },
          {
            id: '3',
            text: 'The planners knew information obtained by Spanish authorities could reach the Germans.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The account describes Spanish cooperation with German intelligence and earlier evidence of information passing through that channel.',
        sectionIds: ['delivery'],
        evidence:
          "The account describes Spanish cooperation with German intelligence and earlier evidence of information passing through that channel. Verified in assigned source section: Developing_the_plan;_the_corpse's_new_identity.",
        sourceSection: {
          title: "Wikipedia: Developing the plan; the corpse's new identity",
          url: "https://en.wikipedia.org/wiki/Operation_Mincemeat#Developing_the_plan;_the_corpse's_new_identity",
        },
      },
      {
        id: 'b3-mincemeat-5',
        prompt:
          'Why place the false target information in natural-sounding personal correspondence?',
        options: [
          {
            id: '0',
            text: 'Such letters could contain no checkable personal detail.',
          },
          {
            id: '1',
            text: 'That form made the information and its means of delivery more plausible.',
          },
          {
            id: '2',
            text: 'Personal letters were guaranteed never to be inspected.',
          },
          {
            id: '3',
            text: 'It allowed the planners to omit any reference to Sicily.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The document needed to identify the target casually but clearly in a form plausibly carried rather than sent by encoded signal.',
        sectionIds: ['documents'],
        evidence:
          'The document needed to identify the target casually but clearly in a form plausibly carried rather than sent by encoded signal. Verified in assigned source section: Deception_documents.',
        sourceSection: {
          title: 'Wikipedia: Deception documents',
          url: 'https://en.wikipedia.org/wiki/Operation_Mincemeat#Deception_documents',
        },
      },
    ],
  },
  {
    id: 'reading-ligo',
    category: 'reading',
    title: 'LIGO',
    topic: 'LIGO',
    difficulty: 'Challenging',
    article: {
      title: 'LIGO',
      url: 'https://en.wikipedia.org/wiki/LIGO',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'Measuring a passing gravitational wave means separating an extraordinarily small signal from moving ground, temperature changes, traffic and even wildlife. The assigned sections explain the instrument and the work required to trust its observations.',
      scope: {
        wordCount: 1758,
        sections: [
          {
            title: 'Mission',
            url: 'https://en.wikipedia.org/wiki/LIGO#Mission',
            wordCount: 404,
            anchors: ['Mission'],
          },
          {
            title: 'Observatories',
            url: 'https://en.wikipedia.org/wiki/LIGO#Observatories',
            wordCount: 341,
            anchors: ['Observatories'],
          },
          {
            title: 'Operation',
            url: 'https://en.wikipedia.org/wiki/LIGO#Operation',
            wordCount: 1013,
            anchors: ['Operation', 'Noise'],
          },
        ],
      },
    },
    reading: {
      title: 'LIGO — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'measure',
          title: 'Compare two light paths',
          blocks: [
            {
              kind: 'paragraph',
              text: 'LIGO uses an interferometer: laser light travels along perpendicular arms and then recombines. Interference depends on the relative phase of the returning light, which reflects how the two paths compare. The instrument is arranged so that the returning beams largely cancel at the signal detector in the reference condition. A passing gravitational wave can change the relative optical paths and alter the recombined light.',
            },
            {
              kind: 'paragraph',
              text: 'The measurement is therefore not a telescope photograph of a distant object and not a direct reading of one mirror’s absolute position. It is a comparison sensitive to very small changes. The same sensitivity makes disturbances of the apparatus important: a signal-like change requires careful analysis before it can be attributed to a cosmic source.',
            },
          ],
        },
        {
          id: 'arms',
          title: 'Repeated travel increases the effective path',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Each physical LIGO arm is kilometers long, and mirrors cause the light to make many trips before it exits. The repeated travel makes the effective optical path much longer than the arm’s single physical length. The assigned article distinguishes these lengths explicitly. Treating the quoted effective path as the distance between buildings would confuse an optical effect with a geographic measurement.',
            },
            {
              kind: 'paragraph',
              text: 'The cavities and recycling arrangement also keep light circulating rather than discarding it after one pass. This improves the instrument’s ability to register small changes in the paths. It does not mean a gravitational wave is trapped inside a mirror cavity in the same way as the laser light. The stored light is the measuring tool; the passing wave is the phenomenon being measured.',
            },
          ],
        },
        {
          id: 'network',
          title: 'Arrival times connect separated observatories',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The two US observatories are widely separated. A gravitational wave can reach them at slightly different times depending on its direction of travel. The article gives a maximum intersite delay of roughly ten milliseconds, consistent with the sites’ separation and propagation at the speed of light. Simultaneous does not need to mean identical clock readings at every site.',
            },
            {
              kind: 'paragraph',
              text: 'Adding another distant instrument supplies another arrival-time comparison and helps locate the source. Multiple observations also strengthen the ability to distinguish a shared astrophysical signal from local disturbances, but geography alone does not certify an event. The signal’s form, environmental monitoring and consistency checks still matter. Timing is useful because the same travelling disturbance encounters detectors at different locations.',
            },
          ],
        },
        {
          id: 'noise',
          title: 'Match the treatment to the disturbance',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article separates noise that can be changed at its source, noise predictable enough to compensate for, and noise measured well enough to estimate its contribution afterward. Repaving a nearby road reduces a vibration source. Predictable Earth-tide stretching can be compensated for by adjusting the instrument. Recorded seismic activity can help estimate its influence on the measured data.',
            },
            {
              kind: 'paragraph',
              text: 'These are different interventions on a causal chain. Better isolation reduces coupling from the environment into the apparatus; monitoring measures disturbances that still occur. Neither assumes that every fluctuation is a gravitational wave. Noise can hide a real signal or imitate one, so improving measurement means controlling both missed detections and false interpretations. A powerful instrument requires an equally careful account of its surroundings.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'LIGO · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/LIGO',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b3-ligo-1',
        prompt: 'What quantity does the interferometer primarily compare to detect a passing wave?',
        options: [
          {
            id: '0',
            text: 'The temperatures of two stars',
          },
          {
            id: '1',
            text: 'The chemical compositions of two mirrors',
          },
          {
            id: '2',
            text: 'The arrival counts of two kinds of cosmic ray',
          },
          {
            id: '3',
            text: 'The relative optical paths of light in perpendicular arms',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'A passing wave changes the relative effective arm lengths and thus the interference of recombined light.',
        sectionIds: ['measure'],
        evidence:
          'A passing wave changes the relative effective arm lengths and thus the interference of recombined light. Verified in assigned source section: Operation.',
        sourceSection: {
          title: 'Wikipedia: Operation',
          url: 'https://en.wikipedia.org/wiki/LIGO#Operation',
        },
      },
      {
        id: 'b3-ligo-2',
        prompt: 'Why can an effective light path be much longer than a physical arm?',
        options: [
          {
            id: '0',
            text: 'Light travels repeatedly between mirrors before leaving the arm.',
          },
          {
            id: '1',
            text: 'The arm expands to the effective length during a detection.',
          },
          {
            id: '2',
            text: 'The observatories are connected by an optical tunnel.',
          },
          {
            id: '3',
            text: 'The laser travels instantaneously on each return trip.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Optical cavities make light traverse the arm many times, increasing its effective path without lengthening the physical arm.',
        sectionIds: ['arms'],
        evidence:
          'Optical cavities make light traverse the arm many times, increasing its effective path without lengthening the physical arm. Verified in assigned source section: Operation.',
        sourceSection: {
          title: 'Wikipedia: Operation',
          url: 'https://en.wikipedia.org/wiki/LIGO#Operation',
        },
      },
      {
        id: 'b3-ligo-3',
        prompt: 'How does another distant detector help estimate where a wave came from?',
        options: [
          {
            id: '0',
            text: 'It measures the source’s distance from brightness alone.',
          },
          {
            id: '1',
            text: 'It provides an additional comparison of arrival times at different locations.',
          },
          {
            id: '2',
            text: 'It supplies a single absolute arrival time that by itself fixes a sky position.',
          },
          {
            id: '3',
            text: 'It locates the source solely from the local ground vibration at the new site.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The source direction can be constrained using arrival-time differences among separated facilities.',
        sectionIds: ['network'],
        evidence:
          'The source direction can be constrained using arrival-time differences among separated facilities. Verified in assigned source section: Observatories.',
        sourceSection: {
          title: 'Wikipedia: Observatories',
          url: 'https://en.wikipedia.org/wiki/LIGO#Observatories',
        },
      },
      {
        id: 'b3-ligo-4',
        prompt: 'Which noise-control example directly changes the local source of disturbance?',
        options: [
          {
            id: '0',
            text: 'Correcting for predicted Earth-tide stretching',
          },
          {
            id: '1',
            text: 'Estimating a seismic contribution from a recorded monitor',
          },
          {
            id: '2',
            text: 'Repaving a nearby road to reduce truck-induced vibration',
          },
          {
            id: '3',
            text: 'Improving suspension to reduce coupling from ground motion',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The noise section describes road repaving as reducing vibration produced by nearby human activity.',
        sectionIds: ['noise'],
        evidence:
          'The noise section describes road repaving as reducing vibration produced by nearby human activity. Verified in assigned source section: Noise.',
        sourceSection: {
          title: 'Wikipedia: Noise',
          url: 'https://en.wikipedia.org/wiki/LIGO#Noise',
        },
      },
      {
        id: 'b3-ligo-5',
        prompt: 'Why monitor the environment even after mechanically isolating the instrument?',
        options: [
          {
            id: '0',
            text: 'Isolation makes the detector insensitive to all remaining motion.',
          },
          {
            id: '1',
            text: 'Only disturbances inside the laser itself can create false signals.',
          },
          {
            id: '2',
            text: 'Any signal seen by one observatory is automatically cosmic.',
          },
          {
            id: '3',
            text: 'Residual or previously unknown couplings may still mimic or obscure a signal.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The article describes monitoring environmental noise and unknown couplings because they can create spurious signals or drown out real ones.',
        sectionIds: ['noise'],
        evidence:
          'The article describes monitoring environmental noise and unknown couplings because they can create spurious signals or drown out real ones. Verified in assigned source section: Noise.',
        sourceSection: {
          title: 'Wikipedia: Noise',
          url: 'https://en.wikipedia.org/wiki/LIGO#Noise',
        },
      },
    ],
  },
  {
    id: 'reading-fairphone',
    category: 'reading',
    title: 'Fairphone',
    topic: 'Fairphone',
    difficulty: 'Challenging',
    article: {
      title: 'Fairphone',
      url: 'https://en.wikipedia.org/wiki/Fairphone',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'What would change if a phone maker designed around repair and supply-chain conditions? This article describes one company’s approach, its practical constraints, and the difference between an ethical aim and a perfect product.',
      scope: {
        wordCount: 906,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Fairphone',
            wordCount: 147,
            anchors: [''],
          },
          {
            title: 'History',
            url: 'https://en.wikipedia.org/wiki/Fairphone#History',
            wordCount: 126,
            anchors: ['History'],
          },
          {
            title: 'Corporate affairs',
            url: 'https://en.wikipedia.org/wiki/Fairphone#Corporate_affairs',
            wordCount: 133,
            anchors: ['Corporate_affairs'],
          },
          {
            title: 'Social impact and competitors',
            url: 'https://en.wikipedia.org/wiki/Fairphone#Social_impact_and_competitors',
            wordCount: 305,
            anchors: ['Social_impact_and_competitors'],
          },
          {
            title: 'Recognition and certifications',
            url: 'https://en.wikipedia.org/wiki/Fairphone#Recognition_and_certifications',
            wordCount: 135,
            anchors: ['Recognition_and_certifications'],
          },
          {
            title: 'Operating systems',
            url: 'https://en.wikipedia.org/wiki/Fairphone#Operating_systems',
            wordCount: 60,
            anchors: ['Operating_systems'],
          },
        ],
      },
    },
    reading: {
      title: 'Fairphone — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'repair',
          title: 'Repair changes the replacement decision',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A modular device separates parts so a failed component can be replaced without discarding the entire phone. Fairphone combines that design with replacement parts and software-support commitments. The mechanism is practical: a physically repairable device remains useful only if the needed part and compatible software are also available.',
            },
            {
              kind: 'paragraph',
              text: 'The environmental motivation concerns the product’s life cycle. Extending useful life can avoid or delay making a replacement device, although the exact impact depends on what is replaced and how the device is used. The article’s specific emissions estimate is attributed to the company. The quiz asks about the mechanism of repair and longevity, not whether one advertised percentage is a universal law for every phone owner.',
            },
          ],
        },
        {
          id: 'limits',
          title: 'A mission is not a claim of perfection',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article quotes a co-founder acknowledging that a completely fair phone was not achievable and describing the ambition comparatively. That qualification matters because electronics supply chains involve many materials, suppliers and labor conditions. Improvement on one dimension does not prove that every stage has become free of harm.',
            },
            {
              kind: 'paragraph',
              text: 'A certification or favorable rating can supply evidence about specified criteria, but it is not logically equivalent to an unlimited guarantee about every ethical question. Read the scope of a claim and who is making it. Calling a product more repairable or documenting a supply-chain improvement can be meaningful without claiming that buying it resolves all environmental and social trade-offs.',
            },
          ],
        },
        {
          id: 'enterprise',
          title: 'Revenue and purpose answer different questions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A social enterprise still needs to sell products and sustain its operations. In the assigned interview excerpt, the distinction is the outcome and priorities pursued, including investment in supply-chain improvement and social innovation. The company is not described as exempt from the need to earn revenue simply because it has an ethical mission.',
            },
            {
              kind: 'paragraph',
              text: 'This separates an organization’s operating mechanism from its purpose. A manufacturer can use familiar business activities while prioritizing different design or purchasing decisions. Neither the label social enterprise nor growing sales by itself establishes that every stated impact has been achieved. The quiz therefore tests the described priority, rather than asking the reader to endorse the company or infer results from its identity alone.',
            },
          ],
        },
        {
          id: 'support',
          title: 'Hardware openness and software support are distinct',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes modular hardware as well as the possibility of running alternative operating systems. These are separate forms of flexibility. Replacing a battery is a hardware action; installing another operating system changes the software environment. The existence of one does not automatically prove the other is available on every device.',
            },
            {
              kind: 'paragraph',
              text: 'Likewise, availability of alternative software is not a promise that every operating system supports every model indefinitely. Compatibility, updates and warranties have specific conditions. The durable idea is that a device’s useful life involves both maintainable components and maintained software. This is why remembering only a repairability score misses part of the article’s account of longevity and user control.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Fairphone · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Fairphone',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b3-fairphone-1',
        prompt: 'How is modular design intended to help a phone remain useful longer?',
        options: [
          {
            id: '0',
            text: 'By bonding more components into a single sealed assembly',
          },
          {
            id: '1',
            text: 'By allowing components to be replaced rather than replacing the entire device',
          },
          {
            id: '2',
            text: 'By making repair depend on replacing the whole main assembly',
          },
          {
            id: '3',
            text: 'By moving repairs exclusively to the original manufacturing plant',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The article connects modular design and replacement parts with user repair and longer useful life.',
        sectionIds: ['repair'],
        evidence:
          'The article connects modular design and replacement parts with user repair and longer useful life. Verified in assigned source section: Social_impact_and_competitors.',
        sourceSection: {
          title: 'Wikipedia: Social impact and competitors',
          url: 'https://en.wikipedia.org/wiki/Fairphone#Social_impact_and_competitors',
        },
      },
      {
        id: 'b3-fairphone-2',
        prompt: 'What qualification did a co-founder make about the company’s ethical ambition?',
        options: [
          {
            id: '0',
            text: 'A phone could be guaranteed to have no negative impact anywhere.',
          },
          {
            id: '1',
            text: 'Only software conditions matter to an ethical assessment.',
          },
          {
            id: '2',
            text: 'The name describes a completed certification of every supplier.',
          },
          {
            id: '3',
            text: 'A completely fair phone was not then achievable; improvement was comparative.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The assigned passage acknowledges the impossibility, at that time, of a 100% fair phone and uses a comparative framing.',
        sectionIds: ['limits'],
        evidence:
          'The assigned passage acknowledges the impossibility, at that time, of a 100% fair phone and uses a comparative framing. Verified in assigned source section: Social_impact_and_competitors.',
        sourceSection: {
          title: 'Wikipedia: Social impact and competitors',
          url: 'https://en.wikipedia.org/wiki/Fairphone#Social_impact_and_competitors',
        },
      },
      {
        id: 'b3-fairphone-3',
        prompt:
          'In the quoted explanation of the social-enterprise model, what differs from a conventional purely technical innovation priority?',
        options: [
          {
            id: '0',
            text: 'Investment in supply-chain improvement and social innovation',
          },
          {
            id: '1',
            text: 'An absence of any need to sell products',
          },
          {
            id: '2',
            text: 'A rule against earning revenue',
          },
          {
            id: '3',
            text: 'A guarantee of annual profitability',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The co-founder says ordinary business needs remain, while the outcomes and investment priorities include social innovation and supply-chain improvement.',
        sectionIds: ['enterprise'],
        evidence:
          'The co-founder says ordinary business needs remain, while the outcomes and investment priorities include social innovation and supply-chain improvement. Verified in assigned source section: Social_impact_and_competitors.',
        sourceSection: {
          title: 'Wikipedia: Social impact and competitors',
          url: 'https://en.wikipedia.org/wiki/Fairphone#Social_impact_and_competitors',
        },
      },
      {
        id: 'b3-fairphone-4',
        prompt: 'Which distinction best matches the article’s treatment of device flexibility?',
        options: [
          {
            id: '0',
            text: 'Replacement parts and operating systems are the same kind of component.',
          },
          {
            id: '1',
            text: 'Alternative operating systems remove the need for maintainable hardware.',
          },
          {
            id: '2',
            text: 'Modular parts support hardware repair; alternative operating systems concern software.',
          },
          {
            id: '3',
            text: 'Running alternative software proves that every model has permanent support.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The article separately describes physical modularity and multiple operating-system options; neither implies unlimited support.',
        sectionIds: ['support'],
        evidence:
          'The article separately describes physical modularity and multiple operating-system options; neither implies unlimited support. Verified in assigned source section: Operating_systems.',
        sourceSection: {
          title: 'Wikipedia: Operating systems',
          url: 'https://en.wikipedia.org/wiki/Fairphone#Operating_systems',
        },
      },
      {
        id: 'b3-fairphone-5',
        prompt:
          'A phone is easy to open, but replacement parts and software updates stop being available. Which inference fits the article’s account of longevity?',
        options: [
          {
            id: '0',
            text: 'Physical access alone guarantees an equally long useful life.',
          },
          {
            id: '1',
            text: 'Repairability works together with parts and software support, so opening the phone is insufficient.',
          },
          {
            id: '2',
            text: 'The absence of parts proves modular design has no possible benefit.',
          },
          {
            id: '3',
            text: 'Alternative software always restores every discontinued hardware component.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The article links longevity to modular repair, spare parts and software support; these are complementary rather than interchangeable.',
        sectionIds: ['repair'],
        evidence:
          'The article links longevity to modular repair, spare parts and software support; these are complementary rather than interchangeable. Verified in assigned source section: Social_impact_and_competitors.',
        sourceSection: {
          title: 'Wikipedia: Social impact and competitors',
          url: 'https://en.wikipedia.org/wiki/Fairphone#Social_impact_and_competitors',
        },
      },
    ],
  },
  {
    id: 'reading-long-now',
    category: 'reading',
    title: 'Long Now Foundation',
    topic: 'Long Now Foundation',
    difficulty: 'Challenging',
    article: {
      title: 'Long Now Foundation',
      url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'A clock intended to outlast civilizations, an archive of endangered languages, and a public meeting place share one institutional idea. Read how these projects approach time horizons much longer than a normal product cycle.',
      scope: {
        wordCount: 833,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation',
            wordCount: 118,
            anchors: [''],
          },
          {
            title: 'Projects',
            url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation#Projects',
            wordCount: 715,
            anchors: [
              'Projects',
              'Clock_of_the_Long_Now',
              'The_Interval',
              'Seminars_about_long-term_thinking',
              'Rosetta_Project',
              'Manual_for_Civilization',
            ],
          },
        ],
      },
    },
    reading: {
      title: 'Long Now Foundation — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'horizon',
          title: 'Represent a longer time horizon',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The foundation promotes thinking across a ten-thousand-year horizon. Its five-digit year notation is a small symbolic expression of that aim: adding a leading digit makes the current year sit inside a longer span. The notation does not change the number of elapsed years or assert that a new historical era began when the foundation was created.',
            },
            {
              kind: 'paragraph',
              text: 'A time horizon is the interval over which consequences are considered. It is different from a prediction that one institution will certainly last that long. The projects use durable objects, archives and discussion to encourage this perspective. The quiz asks what the practices are intended to do, rather than treating the stated horizon as an independently verified forecast of the organization’s future.',
            },
          ],
        },
        {
          id: 'clock',
          title: 'Survival involves incentives as well as materials',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A clock meant to persist over millennia needs more than parts that resist wear. Future people may not understand its purpose or may value its materials for other uses. The article therefore describes plans for durable, repairable and largely low-value materials, together with a renewable power source that is not attractive to loot.',
            },
            {
              kind: 'paragraph',
              text: 'This adds a social condition to an engineering problem. A mechanically durable object can still be dismantled if its components are valuable enough to take. Easy repair also acknowledges that perfect independence from future people is unrealistic. The design intention is minimum intervention over a long period, not a claim that a finished device has already demonstrated ten thousand years of operation.',
            },
          ],
        },
        {
          id: 'language',
          title: 'Preserve records that future readers can access',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The Rosetta Project addresses languages that may lose their communities of speakers. In the article, language samples are inscribed on a nickel-alloy disc, giving the archive a physical form intended to persist. The preservation target is recorded linguistic information; a disc by itself does not keep a language in everyday use among a living community.',
            },
            {
              kind: 'paragraph',
              text: 'That distinction separates a record from the social practice it records. Preserving evidence of a language can help future study without replacing speakers, teaching or cultural transmission. The project’s material choice also fits the broader concern that information should remain accessible across changes in institutions and technology. The quiz does not require memorizing the article’s changing counts of archived languages.',
            },
          ],
        },
        {
          id: 'community',
          title: 'An institution also needs places and shared knowledge',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The Interval is described as a public social space where people can meet around long-term thinking and attend events. It houses artifacts and a library, connecting a large time horizon to a place people can visit now. The foundation’s seminars similarly make the subject an ongoing conversation rather than solely a distant construction project.',
            },
            {
              kind: 'paragraph',
              text: 'The Manual for Civilization collects books intended to preserve useful human and technical knowledge for descendants. Its aim differs from a list of the latest bestsellers or an archive of only the foundation’s own announcements. Together, the social venue, talks and curated library give different roles to gathering people, discussing ideas and preserving knowledge. The article does not establish that any one collection guarantees a civilization can be rebuilt.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Long Now Foundation · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b3-long-now-1',
        prompt: 'What is the purpose of the foundation’s five-digit date notation?',
        options: [
          {
            id: '0',
            text: 'To count years from its own founding instead of the common calendar',
          },
          {
            id: '1',
            text: 'To indicate that the current year is scientifically uncertain',
          },
          {
            id: '2',
            text: 'To place the present within a longer time horizon extending toward year 10,000',
          },
          {
            id: '3',
            text: 'To claim that its projects have already existed for ten millennia',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The article connects five-digit years with the year 10,000 problem and the foundation’s long time horizon.',
        sectionIds: ['horizon'],
        evidence:
          'The article connects five-digit years with the year 10,000 problem and the foundation’s long time horizon. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation',
        },
      },
      {
        id: 'b3-long-now-2',
        prompt: 'Why favor largely low-value materials for the long-lived clock?',
        options: [
          {
            id: '0',
            text: 'To make it less attractive to loot or dismantle for its materials',
          },
          {
            id: '1',
            text: 'To ensure nobody could ever repair it',
          },
          {
            id: '2',
            text: 'To make its timekeeping intentionally inaccurate',
          },
          {
            id: '3',
            text: 'To avoid using a renewable source of power',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The clock design seeks durability and repairability while reducing the incentive for future looting.',
        sectionIds: ['clock'],
        evidence:
          'The clock design seeks durability and repairability while reducing the incentive for future looting. Verified in assigned source section: Clock_of_the_Long_Now.',
        sourceSection: {
          title: 'Wikipedia: Clock of the Long Now',
          url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation#Clock_of_the_Long_Now',
        },
      },
      {
        id: 'b3-long-now-3',
        prompt: 'What does the Rosetta Project seek to preserve in the physical archive described?',
        options: [
          {
            id: '0',
            text: 'The future market prices of rare metals',
          },
          {
            id: '1',
            text: 'A replacement population of native speakers',
          },
          {
            id: '2',
            text: 'Only English translations of the foundation’s lectures',
          },
          {
            id: '3',
            text: 'Samples of languages that may become endangered or extinct',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The article describes language samples inscribed on a nickel-alloy disc as a preservation effort.',
        sectionIds: ['language'],
        evidence:
          'The article describes language samples inscribed on a nickel-alloy disc as a preservation effort. Verified in assigned source section: Rosetta_Project.',
        sourceSection: {
          title: 'Wikipedia: Rosetta Project',
          url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation#Rosetta_Project',
        },
      },
      {
        id: 'b3-long-now-4',
        prompt: 'How does The Interval support the foundation’s mission?',
        options: [
          {
            id: '0',
            text: 'It is primarily the remote construction site of the full-size clock.',
          },
          {
            id: '1',
            text: 'It provides a public social space for long-term-thinking discussions and events.',
          },
          {
            id: '2',
            text: 'It is mainly a storage vault closed to the public.',
          },
          {
            id: '3',
            text: 'It is primarily the workshop that engraves the language discs.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The Interval is described as a public gathering space, with projects, books and events connected to the foundation’s themes.',
        sectionIds: ['community'],
        evidence:
          'The Interval is described as a public gathering space, with projects, books and events connected to the foundation’s themes. Verified in assigned source section: The_Interval.',
        sourceSection: {
          title: 'Wikipedia: The Interval',
          url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation#The_Interval',
        },
      },
      {
        id: 'b3-long-now-5',
        prompt: 'Which book-selection aim best matches the Manual for Civilization?',
        options: [
          {
            id: '0',
            text: 'Collect books according to current sales rankings.',
          },
          {
            id: '1',
            text: 'Store only publications produced by the foundation itself.',
          },
          {
            id: '2',
            text: 'Preserve human and technical knowledge useful to future generations.',
          },
          {
            id: '3',
            text: 'Assemble an exhaustive record of each book published in the current year.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The Manual is a curated record of humanity and technology intended for descendants.',
        sectionIds: ['community'],
        evidence:
          'The Manual is a curated record of humanity and technology intended for descendants. Verified in assigned source section: Manual_for_Civilization.',
        sourceSection: {
          title: 'Wikipedia: Manual for Civilization',
          url: 'https://en.wikipedia.org/wiki/Long_Now_Foundation#Manual_for_Civilization',
        },
      },
    ],
  },
];
